import * as React from 'react'
import { type LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'
import mascot from './empty-mascot.svg'

/**
 * Заранее подтягивает маскота в кэш браузера. Вызывается один раз при старте
 * приложения: тогда первый пустой экран показывает его мгновенно, без подгрузки.
 */
export function preloadEmptyMascot() {
  if (typeof Image === 'undefined') return
  const image = new Image()
  image.decoding = 'async'
  image.src = mascot
}

/**
 * EmptyState — пустое состояние списка/раздела: «пока ничего нет».
 * Маскот слева, справа от него «реплика» в пузыре: заголовок + описание + действие (кнопка).
 * Иллюстрация одна на весь продукт, чтобы пустые экраны выглядели одинаково;
 * пишем живо и по-игровому («Ой, а вы ещё не…»), без канцелярита; точка в конце
 * описания не ставится (компонент сам её срезает).
 * Все части опциональны, кроме title. action — обычно <Button> «Создать…».
 * `size="lg"` — пустота, занимающая всю страницу/вкладку: маскот и реплика крупнее,
 * без вертикальных отступов (центрирует родитель).
 * `size="sm"` — для пустоты внутри небольшой карточки (маскот поменьше, отступы меньше).
 * `size="card"` — маскот крупно и СТОИТ на нижней грани карточки (родитель без нижнего
 * отступа, `relative`, `overflow-hidden`), реплика справа от него.
 */
interface EmptyStateProps {
  /** Устарело и не рисуется: вместо иконки везде маскот. Оставлено, чтобы не ломать вызовы. */
  icon?: LucideIcon
  title: string
  description?: string
  action?: React.ReactNode
  size?: 'default' | 'sm' | 'lg' | 'card'
  /** `stack` — маскот НАД репликой, всё по центру, реплика без хвостика: для
   *  узких мест (панель блока, экран урока), где ряд не помещается. */
  layout?: 'row' | 'stack'
  className?: string
}

export function EmptyState({
  title,
  description,
  action,
  size = 'default',
  layout = 'row',
  className,
}: EmptyStateProps) {
  const small = size === 'sm'
  const large = size === 'lg'
  if (size === 'card')
    return (
      <div className={cn('relative flex min-h-44 flex-1 items-center', className)}>
        <img
          src={mascot}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="absolute bottom-0 left-2 h-auto w-36"
        />
        <div className="relative ml-44 max-w-full rounded-2xl bg-muted px-4 py-3 text-left">
          <span
            aria-hidden="true"
            className="absolute -left-1.5 top-1/2 size-3 -translate-y-1/2 rotate-45 rounded-[2px] bg-muted"
          />
          <h3 className="text-base font-medium text-foreground">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description.replace(/\.$/, '')}</p>
          )}
        </div>
      </div>
    )
  if (layout === 'stack')
    return (
      <div className={cn('flex flex-col items-center gap-4 py-6 text-center', className)}>
        <img
          src={mascot}
          alt=""
          aria-hidden="true"
          draggable={false}
          className="h-auto w-40 shrink-0"
        />
        <div className="max-w-full rounded-2xl bg-muted px-5 py-4">
          <h3 className="text-base font-medium text-foreground">{title}</h3>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description.replace(/\.$/, '')}</p>
          )}
          {action && <div className="mt-4 flex justify-center">{action}</div>}
        </div>
      </div>
    )
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center sm:flex-row',
        small ? 'py-6 gap-3 sm:gap-4' : large ? 'gap-6 sm:gap-10' : 'py-14 gap-5 sm:gap-8',
        className,
      )}
    >
      <img
        src={mascot}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={cn('h-auto shrink-0', small ? 'w-24' : large ? 'w-72' : 'w-48')}
      />
      <div
        className={cn(
          'relative max-w-full rounded-2xl bg-muted text-left',
          // Ширина пузыря фиксирована: иначе от длины текста сдвигается вся группа,
          // и маскот «прыгает» при переключении вкладок.
          small ? 'sm:w-72' : large ? 'sm:w-[32rem]' : 'sm:w-[26rem]',
          small ? 'px-4 py-3' : large ? 'px-8 py-7' : 'px-6 py-5',
        )}
      >
        {/* Хвостик реплики — к маскоту (на узких экранах реплика под ним, без хвоста). */}
        <span
          aria-hidden="true"
          className="absolute -left-1.5 top-1/2 hidden size-3 -translate-y-1/2 rotate-45 rounded-[2px] bg-muted sm:block"
        />
        <h3 className={cn('font-medium text-foreground', large ? 'text-xl' : 'text-base')}>
          {title}
        </h3>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description.replace(/\.$/, '')}</p>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  )
}
